import { useEffect, useRef, useState } from "react";
import "./StrippingEfficiencyChart.css";
import { X } from "lucide-react";

function StrippingEfficiencyChart({ onClose }) {

  // ================= DRAG SYSTEM =================
  const [position, setPosition] = useState({ x: 250, y: 120 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  const startDrag = (e) => {
    setDragging(true);
    setOffset({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  useEffect(() => {
    const move = (e) => {
      if (!dragging) return;
      setPosition({
        x: e.clientX - offset.x,
        y: e.clientY - offset.y
      });
    };

    const stop = () => setDragging(false);

    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", stop);

    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", stop);
    };
  }, [dragging, offset]);

  // ================= TAG INFO =================
  const tag = "AIT_2902";
  const range = "0 - 50 ppm";
  const description =
    "H2S concentration in treated water (Stripping efficiency analyzer)";

  // ================= TIME WINDOW =================
  const [timeWindow, setTimeWindow] = useState("1hr");

  const timeMap = {
    "1min": 60,
    "30min": 1800,
    "1hr": 3600,
    "5hr": 18000,
    "1day": 86400,
    "30days": 2592000
  };

  const maxPoints = 120;

  // ================= HISTORIAN DATA =================
  const [data, setData] = useState([]);

  // ================= REAL-TIME SIMULATION =================
  useEffect(() => {

    const interval = setInterval(() => {

      const t = Date.now() / 1000;

      // ===== ideal sweet spot gaussian behavior =====
      const idealCenter = 5.3; // pH sweet spot reference (scaled behavior)
      const sigma = 1.2;

      // simulate fluctuating H2S (real DCS noise + process response)
      const base =
        10 +
        Math.sin(t / 10) * 1.5 +
        Math.random() * 0.8;

      const gaussianPenalty =
        Math.exp(-Math.pow((base - idealCenter) / sigma, 2));

      const h2s =
        base * (1 - gaussianPenalty * 0.4);

      setData(prev => {
        const next = [...prev, h2s];
        return next.slice(-maxPoints);
      });

    }, 1000);

    return () => clearInterval(interval);

  }, []);

  // ================= TIME SCALE =================
  const visibleData = data.slice(-maxPoints);

  // ================= GAUSSIAN CURVE (IDEAL MODEL) =================
  const gaussian = (x, mu, sigma) =>
    Math.exp(-Math.pow((x - mu) / sigma, 2));

  const idealCenter = 10;
  const sigma = 2;

  // ================= RENDER =================
  return (
    <div
      className="se-chart-container"
      style={{
        left: position.x,
        top: position.y
      }}
    >

      {/* HEADER */}
      <div
        className="se-chart-header"
        onMouseDown={startDrag}
      >
        <span>Stripping Efficiency Analyzer</span>

        <button className="se-close-btn" onClick={onClose}>
          <X size={14} />
        </button>
      </div>

      {/* INFO */}
      <div className="se-info">
        <div><b>Tag:</b> {tag}</div>
        <div><b>Range:</b> {range}</div>
        <div><b>Description:</b> {description}</div>
      </div>

      {/* GRAPH AREA */}
      <div className="se-graph">

        {/* LIVE BARS */}
        {visibleData.map((v, i) => (
          <div
            key={i}
            className="se-bar"
            style={{
              height: `${v * 6}px`
            }}
          />
        ))}

      </div>

      {/* GAUSSIAN OVERLAY (IDEAL CURVE VISUAL) */}
      <div className="se-gaussian-overlay">
        {Array.from({ length: 40 }, (_, i) => {
          const x = (i / 40) * 20;
          const y = gaussian(x, idealCenter, sigma) * 100;

          return (
            <div
              key={i}
              className="se-gauss-point"
              style={{
                left: `${i * 2.5}%`,
                height: `${y}px`
              }}
            />
          );
        })}
      </div>

      {/* TIME CONTROLS */}
      <div className="se-time-controls">
        {Object.keys(timeMap).map(t => (
          <button
            key={t}
            className={timeWindow === t ? "active" : ""}
            onClick={() => setTimeWindow(t)}
          >
            {t}
          </button>
        ))}
      </div>

    </div>
  );
}

export default StrippingEfficiencyChart;